<script lang="ts" setup>
import { computed, ref } from 'vue';
import { type RouteLocationRaw } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useI18n } from 'vue-i18n';
import { getHomeQuickLinks, type GetHomeQuickLinksItemResponse } from '@/api';
import { getCustomIcon } from '@/common/custom-icons.ts';
import { useSsgState } from '@/utilities/ssg-state.ts';

const MaxNarrowRowSize = 5;
const narrowRowClasses = ['basis-full', 'basis-full', 'basis-1/2', 'basis-1/3', 'basis-1/4', 'basis-1/5'];

const { t } = useI18n();

const quickLinks = ref<GetHomeQuickLinksItemResponse[] | null>(null);

const loadQuickLinks = async () => {
  const result = await getHomeQuickLinks();
  if (result.response?.ok && result.data) {
    quickLinks.value = result.data.items;
    return;
  }

  console.error(`Fetching home quick links failed: ${result.error}`);
};

useSsgState({
  key: 'home-quick-links',
  refs: { quickLinks },
  isLoaded: () => quickLinks.value !== null,
  load: loadQuickLinks,
});

// Sur un écran étroit, une rangée ne tient pas plus de cinq liens : au-delà, ils sont répartis en rangées égales.
const narrowRowClass = computed(() => {
  const count = quickLinks.value?.length ?? 0;
  const rowSize = count <= MaxNarrowRowSize ? count : Math.ceil(count / Math.ceil(count / MaxNarrowRowSize));
  return narrowRowClasses[rowSize] ?? 'basis-full';
});

const toSearchRoute = (quickLink: GetHomeQuickLinksItemResponse): RouteLocationRaw => ({
  name: 'search',
  query: {
    ...(quickLink.categoryId !== null ? { categoryIds: quickLink.categoryId } : {}),
    ...(quickLink.priceTypeId !== null ? { priceTypeId: quickLink.priceTypeId } : {}),
  },
});
</script>

<template>
  <nav v-if="quickLinks && quickLinks.length > 0" :aria-label="t('views.home.hero.quick-links')" class="no-print relative z-10 border-b border-gray-200 bg-white shadow-xs lg:-mt-8 lg:border-b-0 lg:bg-transparent lg:px-4 lg:shadow-none">
    <ul class="flex flex-wrap lg:mx-auto lg:max-w-6xl lg:flex-nowrap lg:overflow-hidden lg:rounded-lg lg:border lg:border-gray-200 lg:bg-white lg:shadow-lg">
      <li v-for="quickLink in quickLinks" :key="quickLink.id" :class="narrowRowClass" class="min-w-0 grow border-l border-gray-100 first:border-l-0 lg:basis-0">
        <RouterLink
          :to="toSearchRoute(quickLink)"
          class="hover:bg-ph-green-50 active:bg-ph-green-100 focus-visible:outline-ph-green-600 flex h-full min-h-20 flex-col items-center gap-1.5 px-0.5 pt-3 pb-2.5 text-center transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 motion-reduce:transition-none lg:min-h-15 lg:flex-row lg:justify-center lg:gap-2 lg:px-2 lg:py-2 lg:text-left"
        >
          <FontAwesomeIcon v-if="quickLink.customIcon" :icon="getCustomIcon(quickLink.customIcon)" aria-hidden="true" class="text-ph-green-700 shrink-0 text-2xl lg:text-xl" />
          <span aria-hidden="true" class="flex max-w-full flex-col lg:hidden">
            <span class="2xs:text-[0.71875rem]/[1.2] text-[0.6875rem]/[1.2] font-semibold tracking-tight wrap-break-word text-gray-900">{{ quickLink.shortName }}</span>
            <span v-if="quickLink.shortDetail" class="text-[0.6875rem]/[1.3] wrap-break-word text-gray-500">{{ quickLink.shortDetail }}</span>
          </span>
          <span class="sr-only lg:not-sr-only lg:text-[0.8125rem]/[1.25] lg:font-semibold lg:text-gray-900">{{ quickLink.name }}</span>
        </RouterLink>
      </li>
    </ul>
  </nav>
</template>

<style lang="css" scoped></style>
